import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';

/**
 * Custel Toast — handoff `components.md "SnackBar/Toast"` spec.
 *
 * iOS `ToastManager` ile parite:
 *   - 4 variant: success, warning, error, info
 *   - default 4s duration (Sprint 11 P2-02 fix — pilot W9-10 feedback)
 *   - dismiss button + auto-dismiss
 *   - top-right stacking
 *
 * Kullanım:
 *   <ToastProvider>
 *     <App />
 *   </ToastProvider>
 *
 *   const { toast } = useToast();
 *   toast({ variant: 'success', title: 'Fiş yüklendi', message: 'ETTN: ABC123...' });
 */

export type ToastVariant = 'success' | 'warning' | 'error' | 'info';

export interface ToastOptions {
  variant?: ToastVariant;
  title: string;
  message?: string;
  /** ms — default 4000. 0 = manuel dismiss only */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * BRANDBOOK §6.10 — Toast: paper bg + border-left 4px semantic color.
 * Icon semantic renkte (success/warning/danger/info), title/body ink/g700.
 */
const VARIANT_STYLES: Record<
  ToastVariant,
  { Icon: typeof CheckCircle2; iconCls: string; borderLeftCls: string; ariaLive: 'polite' | 'assertive' }
> = {
  success: {
    Icon: CheckCircle2,
    iconCls: 'text-custel-success',
    borderLeftCls: 'border-l-custel-success',
    ariaLive: 'polite',
  },
  warning: {
    Icon: AlertTriangle,
    iconCls: 'text-custel-warning',
    borderLeftCls: 'border-l-custel-warning',
    ariaLive: 'polite',
  },
  error: {
    Icon: XCircle,
    iconCls: 'text-custel-danger',
    borderLeftCls: 'border-l-custel-danger',
    ariaLive: 'assertive',
  },
  info: {
    Icon: Info,
    iconCls: 'text-custel-info',
    borderLeftCls: 'border-l-custel-info',
    ariaLive: 'polite',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('components');
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const item: ToastItem = { ...options, id };
      setItems((prev) => [...prev, item]);
      // BRANDBOOK §6.10 — danger default 8s, diğerleri 4s. Manuel `duration` her zaman önceliklidir.
      const defaultDuration = options.variant === 'error' ? 8000 : 4000;
      const duration = options.duration ?? defaultDuration;
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      {/* BRANDBOOK §6.10 — toast region bottom-right (16px), column-reverse stacking */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[2000] flex w-80 max-w-[calc(100vw-2rem)] flex-col-reverse gap-2"
        role="region"
        aria-label={t('toast.region')}
      >
        {items.map((item) => (
          <ToastCard key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const { t } = useTranslation('components');
  const { Icon, iconCls, borderLeftCls, ariaLive } = VARIANT_STYLES[item.variant ?? 'info'];
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setEntered(true));
  }, []);

  return (
    <div
      role="alert"
      aria-live={ariaLive}
      className={cn(
        // BRANDBOOK §6.10 — paper bg + border-left 4px semantic + radius 10 + shadow-toast
        'pointer-events-auto flex min-w-[280px] max-w-[400px] items-start gap-2.5 rounded-custel-lg border-l-4 bg-custel-paper p-3 shadow-custel-toast transition-all duration-custel-base',
        borderLeftCls,
        entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconCls)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-custel-ink">{item.title}</p>
        {item.message && <p className="mt-0.5 text-[13px] text-custel-g700">{item.message}</p>}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded p-0.5 text-custel-g500 hover:bg-custel-g100 hover:text-custel-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-custel-ink"
        aria-label={t('toast.dismiss')}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() ToastProvider içinde çağrılmalı.');
  }
  return ctx;
}
